// ФАЙЛ: lottieCache.ts
// НАЗНАЧЕНИЕ: In-memory LRU-кэш распарсенного lottie JSON, чтобы повторный
// триггер того же эффекта не перекачивал ассет заново.
//
// Ключ кэша — effectAssetId (стабильный), а НЕ mediaUrl: presigned URL
// меняется при каждом триггере (в нём подпись), поэтому по нему кэшировать нельзя.
//
// LRU реализован через порядок вставки в Map: при обращении запись
// «омолаживается» (удаляется и вставляется заново → уходит в конец), при
// переполнении вытесняется самая старая (первый ключ). У каждой записи есть TTL.

const MAX_ENTRIES = 10
const TTL_MS = 5 * 60 * 1_000

interface CacheEntry {
  animationData: unknown
  cachedAt: number
}

const cache = new Map<string, CacheEntry>()

/**
 * Возвращает закэшированный animationData по effectAssetId либо undefined,
 * если записи нет или она протухла (старше TTL). При попадании запись
 * «омолаживается», чтобы не быть вытесненной как самая старая.
 */
export function getCachedLottie(effectAssetId: string): unknown | undefined {
  const entry = cache.get(effectAssetId)
  if (!entry) return undefined

  if (Date.now() - entry.cachedAt > TTL_MS) {
    cache.delete(effectAssetId)
    return undefined
  }

  // Омолаживаем: перемещаем в конец очереди LRU.
  cache.delete(effectAssetId)
  cache.set(effectAssetId, entry)
  return entry.animationData
}

/**
 * Кладёт animationData в кэш под effectAssetId. При переполнении вытесняет
 * самую старую запись (первый ключ в порядке вставки).
 */
export function setCachedLottie(effectAssetId: string, animationData: unknown): void {
  // Перевставка, чтобы обновлённая запись оказалась в конце очереди.
  if (cache.has(effectAssetId)) cache.delete(effectAssetId)
  cache.set(effectAssetId, { animationData, cachedAt: Date.now() })

  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
}

/** Полностью очищает кэш. Используется в тестах для изоляции. */
export function clearLottieCache(): void {
  cache.clear()
}
